import { Spinner, Tooltip } from "@gotts.ai/ui";
import { useState } from "react";

import { LiquidityHistogram } from "../../components/LiquidityHistogram";
import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment } from "../../context";
import { TOKEN_DECIMALS } from "../../lib/constants";
import {
  formatFeePercent,
  formatLiquidity,
  formatTokenAmount,
} from "../../lib/format";

import { MAX_TICK, MIN_TICK, resolveSymbol, tickToPrice } from "./types";

import type { V3Position } from "./types";
import type { Address } from "viem";

interface V3PositionsSectionProps {
  positions: V3Position[];
  loading: boolean;
  poolTicks: Record<string, number>;
  liqBuckets: { tick: number; liquidity: number }[];
  liqLoading: boolean;
  isConnected: boolean;
  onCollectFees: (tokenId: bigint) => void;
  onRemove?: (position: V3Position) => void;
  isExecuting: boolean;
}

export function V3PositionsSection({
  positions,
  loading,
  poolTicks,
  liqBuckets,
  liqLoading,
  isConnected,
  onCollectFees,
  onRemove,
  isExecuting,
}: V3PositionsSectionProps) {
  const { deployment } = useDeployment();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Spinner />
        <span className="text-text-muted text-sm">Loading positions...</span>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <p className="text-text-muted py-4 text-sm">
        Connect a wallet to view V3 positions.
      </p>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="py-4">
        <p className="text-text-muted text-sm">
          Provide concentrated liquidity on V3.
        </p>
        <p className="text-text-muted mt-1 text-xs">
          Use the{" "}
          <a
            href="#add-liquidity-form"
            className="text-gold underline underline-offset-2"
          >
            Add Liquidity
          </a>{" "}
          form above and select the V3 tab.
        </p>
      </div>
    );
  }

  // Resolve token symbols for histogram
  const tokens = deployment?.tokens as Record<string, Address> | undefined;
  const firstPos = positions[0] as (typeof positions)[0] | undefined;
  const histSym0 =
    tokens && firstPos ? resolveSymbol(firstPos.token0, tokens) : undefined;
  const histSym1 =
    tokens && firstPos ? resolveSymbol(firstPos.token1, tokens) : undefined;
  const histDec0 = histSym0 ? (TOKEN_DECIMALS[histSym0] ?? 18) : undefined;
  const histDec1 = histSym1 ? (TOKEN_DECIMALS[histSym1] ?? 18) : undefined;

  return (
    <div className="space-y-4">
      <div className="lp-info-banner">
        <svg
          className="lp-info-banner-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        V3 positions are concentrated within a price range. Fees accrue
        separately and must be collected.
      </div>

      {/* Liquidity Density Histogram */}
      {liqBuckets.length > 0 && Object.keys(poolTicks).length > 0 ? (
        <div>
          <div className="section-tag mb-2">Liquidity Distribution</div>
          <LiquidityHistogram
            buckets={liqBuckets}
            currentTick={Object.values(poolTicks)[0] ?? 0}
            positionTickLower={positions[0]?.tickLower}
            positionTickUpper={positions[0]?.tickUpper}
            token0Symbol={histSym0}
            token1Symbol={histSym1}
            decimals0={histDec0}
            decimals1={histDec1}
          />
        </div>
      ) : liqLoading ? (
        <div className="flex items-center gap-2 py-4">
          <Spinner />
          <span className="text-text-muted text-sm">
            Loading liquidity data...
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-center rounded border border-dashed border-[var(--glass-border)] py-6">
          <span className="text-[12px] text-[var(--color-text-muted)]">
            No liquidity distribution data available
          </span>
        </div>
      )}

      {/* Position cards */}
      <div className="space-y-3">
        {positions.map((pos) => {
          const posTokens = deployment?.tokens as
            | Record<string, Address>
            | undefined;
          const sym0 = posTokens
            ? resolveSymbol(pos.token0, posTokens)
            : pos.token0.slice(0, 8);
          const sym1 = posTokens
            ? resolveSymbol(pos.token1, posTokens)
            : pos.token1.slice(0, 8);
          const hasFees = pos.tokensOwed0 > 0n || pos.tokensOwed1 > 0n;

          const posDec0 = TOKEN_DECIMALS[sym0] ?? 18;
          const posDec1 = TOKEN_DECIMALS[sym1] ?? 18;
          const isFullRange =
            pos.tickLower <= MIN_TICK && pos.tickUpper >= MAX_TICK;
          const priceLow = tickToPrice(pos.tickLower, posDec0, posDec1);
          const priceHigh = tickToPrice(pos.tickUpper, posDec0, posDec1);

          const poolKey = `${pos.token0}:${pos.token1}:${String(pos.fee)}`;
          const currentTick = poolTicks[poolKey];
          const inRange =
            currentTick !== undefined
              ? pos.tickLower <= currentTick && currentTick < pos.tickUpper
              : undefined;

          const isExpanded = expandedId === pos.tokenId.toString();
          const rangeClass =
            inRange === true
              ? "lp-card-in-range"
              : inRange === false
                ? "lp-card-out-of-range"
                : "";

          return (
            <div
              key={pos.tokenId.toString()}
              className={`lp-card lp-card-v3 ${rangeClass}`}
            >
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="lp-pair-icons">
                    <TokenIcon symbol={sym0} size={32} />
                    <TokenIcon symbol={sym1} size={32} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-mono text-sm font-semibold text-[var(--color-gold)]">
                      {sym0}/{sym1}
                    </span>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span
                        className="protocol-pill protocol-pill-v3"
                        style={{ fontSize: 9, padding: "1px 6px" }}
                      >
                        V3
                      </span>
                      <span className="lp-fee-pill">
                        {formatFeePercent(pos.fee)}
                      </span>
                      <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                        #{pos.tokenId.toString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {inRange === true && (
                    <span className="badge-in-range">
                      <span className="range-dot range-dot-in" />
                      In Range
                    </span>
                  )}
                  {inRange === false && (
                    <span className="badge-out-of-range">
                      <span className="range-dot range-dot-out" />
                      Out of Range
                    </span>
                  )}
                </div>
              </div>

              {/* Price range + liquidity row */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {/* Price range */}
                <div className="lp-price-range">
                  {isFullRange ? (
                    <div className="flex w-full items-center justify-center py-1">
                      <span className="badge-position-type">Full Range</span>
                    </div>
                  ) : (
                    <>
                      <div className="lp-price-range-bound">
                        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                          Min
                        </span>
                        <span className="font-mono text-xs text-[var(--color-text)]">
                          {priceLow}
                        </span>
                      </div>
                      <span className="lp-price-range-arrow">{"\u2192"}</span>
                      <div className="lp-price-range-bound">
                        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                          Max
                        </span>
                        <span className="font-mono text-xs text-[var(--color-text)]">
                          {priceHigh}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Liquidity value */}
                <div className="flex flex-col items-end justify-center">
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    Liquidity
                  </span>
                  <Tooltip content={pos.liquidity.toString()}>
                    <span className="cursor-help font-mono text-sm font-medium text-[var(--color-text)]">
                      {formatLiquidity(pos.liquidity)}
                    </span>
                  </Tooltip>
                </div>
              </div>

              {/* Fees owed */}
              <div className="mt-3 flex items-center justify-between rounded-sm bg-[rgba(255,255,255,0.015)] px-3 py-2">
                <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                  Fees Earned
                </span>
                <span
                  className="font-mono text-xs text-[var(--color-text-dim)]"
                  data-testid={`fees-owed-${pos.tokenId.toString()}`}
                >
                  {formatTokenAmount(pos.tokensOwed0, posDec0).display} {sym0}
                  {" / "}
                  {formatTokenAmount(pos.tokensOwed1, posDec1).display} {sym1}
                </span>
              </div>

              {/* Actions */}
              <div className="lp-actions">
                <button
                  type="button"
                  className="btn-ghost rounded-sm px-3 text-[10px]"
                  onClick={() => {
                    setExpandedId(isExpanded ? null : pos.tokenId.toString());
                  }}
                >
                  {isExpanded ? "Hide Details" : "Details"}
                </button>
                <div className="flex-1" />
                {hasFees ? (
                  <button
                    type="button"
                    className="btn-success-outline rounded-sm px-3"
                    onClick={() => {
                      onCollectFees(pos.tokenId);
                    }}
                    disabled={isExecuting}
                  >
                    Collect Fees
                  </button>
                ) : (
                  <Tooltip content="No fees to collect">
                    <span>
                      <button
                        type="button"
                        className="btn-success-outline rounded-sm px-3 opacity-40"
                        disabled
                      >
                        Collect Fees
                      </button>
                    </span>
                  </Tooltip>
                )}
                {onRemove && (
                  <button
                    type="button"
                    className="btn-remove-destructive"
                    onClick={() => {
                      onRemove(pos);
                    }}
                    disabled={isExecuting || pos.liquidity === 0n}
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Expandable details */}
              <div
                className="lp-detail-expand"
                data-open={isExpanded ? "true" : "false"}
              >
                <div>
                  <div className="lp-detail-grid">
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Token ID</span>
                      <span className="lp-detail-item-value">
                        {pos.tokenId.toString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">
                        Liquidity (raw)
                      </span>
                      <span className="lp-detail-item-value">
                        {pos.liquidity.toString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Tick Lower</span>
                      <span className="lp-detail-item-value">
                        {pos.tickLower.toLocaleString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">Tick Upper</span>
                      <span className="lp-detail-item-value">
                        {pos.tickUpper.toLocaleString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">
                        Tokens Owed 0
                      </span>
                      <span className="lp-detail-item-value">
                        {pos.tokensOwed0.toString()}
                      </span>
                    </div>
                    <div className="lp-detail-item">
                      <span className="lp-detail-item-label">
                        Tokens Owed 1
                      </span>
                      <span className="lp-detail-item-value">
                        {pos.tokensOwed1.toString()}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="btn-ghost rounded-sm px-2 text-[10px]"
                      onClick={() =>
                        navigator.clipboard.writeText(
                          JSON.stringify(
                            {
                              tokenId: pos.tokenId.toString(),
                              tickLower: pos.tickLower,
                              tickUpper: pos.tickUpper,
                              liquidity: pos.liquidity.toString(),
                              feeGrowthInside0LastX128:
                                pos.feeGrowthInside0LastX128.toString(),
                              feeGrowthInside1LastX128:
                                pos.feeGrowthInside1LastX128.toString(),
                              tokensOwed0: pos.tokensOwed0.toString(),
                              tokensOwed1: pos.tokensOwed1.toString(),
                            },
                            null,
                            2,
                          ),
                        )
                      }
                    >
                      Copy JSON
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
